import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { useEffect, useState } from "react";
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";
import {
  updateBookingPropertyURL,
  cancellationPolicyURL,
  uploadBookingPropertyImagesURL,
  deleteBookingPropertyImageURL
} from "../../../utils/urls";

const COMMISSION_TYPE_OPTIONS = [
  { label: "Percentage", value: "percentage" },
  { label: "Fixed Amount", value: "fixed" }
];

const emptyTier = () => ({ minNights: 1, maxNights: null, type: "percentage", value: 10 });

const EditBookingPropertyForm = ({ visible, onHide, unit, facilityId, currencies, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [namedCancellationPolicies, setNamedCancellationPolicies] = useState([]);
  const [commissionTiers, setCommissionTiers] = useState([]);
  // Photos shown to guests on the external booking API / WordPress widget
  const [images, setImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);

  // Initialize form state
  const [form, setForm] = useState({
    description: "",
    propertyType: "",
    propertyName: "",
    amenities: [],
    basePrice: 0,
    currencyId: "",
    weekendPriceAdjustment: 0,
    minimumStay: 1,
    maximumStay: 30,
    advanceBookingDays: 0,
    commission: 10,
    cancellationPolicyId: "",
    isListed: true,
    status: "Active"
  });

  const propertyTypes = [
    { label: "Apartment", value: "Apartment" },
    { label: "House", value: "House" },
    { label: "Studio", value: "Studio" },
    { label: "One Bedroom", value: "One Bedroom" },
    { label: "Two Bedroom", value: "Two Bedroom" },
    { label: "Three Bedroom", value: "Three Bedroom" },
    { label: "Office", value: "Office" },
    { label: "Store", value: "Store" },
    { label: "Warehouse", value: "Warehouse" }
  ];

  const amenitiesOptions = [
    { label: "WiFi", value: "WiFi" },
    { label: "Air Conditioning", value: "Air Conditioning" },
    { label: "Heating", value: "Heating" },
    { label: "Kitchen", value: "Kitchen" },
    { label: "TV", value: "TV" },
    { label: "Parking", value: "Parking" },
    { label: "Elevator", value: "Elevator" },
    { label: "Washing Machine", value: "Washing Machine" },
    { label: "Security", value: "Security" }
  ];

  // Fetch the facility's named cancellation policies for assignment
  const fetchNamedCancellationPolicies = async () => {
    try {
      const response = await makeRequest2(`${cancellationPolicyURL}/${facilityId}`, "GET");
      if (response.success) {
        const list = response.data?.data || response.data || [];
        setNamedCancellationPolicies(Array.isArray(list) ? list : []);
      }
    } catch (error) {
      console.error("Error fetching cancellation policies:", error);
    }
  };

  // Initialize form with property data
  useEffect(() => {
    if (unit && unit.bookingProperty && visible) {
      console.log("Unit data to populate form:", JSON.stringify(unit, null, 2));

      fetchNamedCancellationPolicies();

      const propertyData = unit.bookingProperty;

      // Extract currency ID
      const currencyId = extractCurrencyId(propertyData.currencyId);

      // Set form values
      setForm({
        description: propertyData.description || "",
        propertyName: propertyData.propertyName || unit.name || "",
        propertyType: propertyData.propertyType || unit.unitType || "",
        amenities: Array.isArray(propertyData.amenities) ? propertyData.amenities : [],
        basePrice: typeof propertyData.basePrice === 'number' ? propertyData.basePrice : 0,
        currencyId: currencyId,
        weekendPriceAdjustment: typeof propertyData.weekendPriceAdjustment === 'number' ? propertyData.weekendPriceAdjustment : 0,
        minimumStay: typeof propertyData.minimumStay === 'number' ? propertyData.minimumStay : 1,
        maximumStay: typeof propertyData.maximumStay === 'number' ? propertyData.maximumStay : 30,
        advanceBookingDays: typeof propertyData.advanceBookingDays === 'number' ? propertyData.advanceBookingDays : 0,
        commission: typeof propertyData.commission === 'number' ? propertyData.commission : 10,
        cancellationPolicyId: extractCurrencyId(propertyData.cancellationPolicyId),
        isListed: propertyData.isListed === true,
        status: propertyData.status || "Active"
      });

      setCommissionTiers(Array.isArray(propertyData.commissionTiers) ? propertyData.commissionTiers : []);
      setImages(Array.isArray(propertyData.images) ? propertyData.images : []);
    }
  }, [unit, visible]);

  // Helper function to extract currency ID
  const extractCurrencyId = (currencyData) => {
    if (!currencyData) return "";

    if (typeof currencyData === 'string') {
      return currencyData;
    }

    if (currencyData._id) {
      return currencyData._id;
    }

    if (currencyData.id) {
      return currencyData.id;
    }

    return "";
  };

  const handleAddCommissionTier = () => {
    setCommissionTiers((prev) => [...prev, emptyTier()]);
  };

  const handleRemoveCommissionTier = (index) => {
    setCommissionTiers((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCommissionTierChange = (index, field, value) => {
    setCommissionTiers((prev) =>
      prev.map((tier, i) => (i === index ? { ...tier, [field]: value } : tier))
    );
  };

  // Get selected currency symbol
  const getSelectedCurrencySymbol = () => {
    const selectedCurrency = currencies.find(c => c._id === form.currencyId);
    return selectedCurrency ? (selectedCurrency.symbol || selectedCurrency.code) : '';
  };

  const handleFormChange = (field, value) => {
    setForm({
      ...form,
      [field]: value
    });
  };

  const getPropertyId = () => unit.bookingProperty?.id || unit.bookingProperty?._id;

  const handleImagesSelected = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const propertyId = getPropertyId();
    if (!propertyId) {
      toastify("Save the property before uploading photos", "error");
      return;
    }

    const formData = new FormData();
    files.forEach((file) => formData.append("images", file));

    try {
      setUploadingImages(true);
      const response = await makeRequest2(
        `${uploadBookingPropertyImagesURL}/${facilityId}/${propertyId}`,
        "POST",
        formData
      );

      if (response.success) {
        setImages(response.data?.data?.images || []);
        toastify("Photos uploaded successfully", "success");
      } else {
        toastify(response.error || "Failed to upload photos", "error");
      }
    } catch (uploadError) {
      console.error("Error uploading property images:", uploadError);
      toastify("Failed to upload photos", "error");
    } finally {
      setUploadingImages(false);
      event.target.value = "";
    }
  };

  const handleRemoveImage = async (imageUrl) => {
    const propertyId = getPropertyId();
    if (!propertyId) return;

    try {
      const response = await makeRequest2(
        `${deleteBookingPropertyImageURL}/${facilityId}/${propertyId}`,
        "POST",
        { imageUrl }
      );

      if (response.success) {
        setImages(response.data?.data?.images || []);
        toastify("Photo removed", "success");
      } else {
        toastify(response.error || "Failed to remove photo", "error");
      }
    } catch (deleteError) {
      console.error("Error removing property image:", deleteError);
      toastify("Failed to remove photo", "error");
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      const requiredFields = ['propertyName', 'propertyType', 'basePrice', 'currencyId'];

      const missingFields = requiredFields.filter(field => !form[field]);

      if (missingFields.length > 0) {
        const errorMsg = `Missing required fields: ${missingFields.join(', ')}`;
        setError(errorMsg);
        toastify(errorMsg, "error");
        return;
      }

      if (!currencies.some(curr => curr._id === form.currencyId)) {
        setError("Please select a valid currency");
        toastify("Please select a valid currency", "error");
        return;
      }

      const invalidTier = commissionTiers.find(
        (tier) => !tier.minNights || tier.minNights < 1 || tier.value === null || tier.value === undefined || tier.value < 0
      );
      if (invalidTier) {
        setError("Each commission tier needs a valid minimum nights and value");
        toastify("Each commission tier needs a valid minimum nights and value", "error");
        return;
      }

      // All units are Swan-managed - the landlord-managed option no longer exists.
      const payload = {
        managedByLandlord: false,
        description: form.description,
        propertyName: form.propertyName,
        propertyType: form.propertyType,
        amenities: form.amenities,
        isListed: form.isListed,
        status: form.status,
        cancellationPolicyId: form.cancellationPolicyId || null,
        basePrice: form.basePrice,
        currencyId: form.currencyId,
        weekendPriceAdjustment: form.weekendPriceAdjustment,
        minimumStay: form.minimumStay,
        maximumStay: form.maximumStay,
        advanceBookingDays: form.advanceBookingDays,
        commission: form.commission,
        commissionTiers
      };

      console.log("Updating booking property with payload:", payload);

      // Get the property ID from unit.bookingProperty
      const propertyId = unit.bookingProperty?.id || unit.bookingProperty?._id;

      if (!propertyId) {
        throw new Error("Property ID is missing");
      }

      const response = await makeRequest2(
        `${updateBookingPropertyURL}/${facilityId}/${propertyId}`,
        "PUT",
        payload
      );

      if (response.success) {
        toastify("Property updated successfully", "success");
        if (onSuccess) onSuccess(response.data);
      } else {
        throw new Error(response.error || "Failed to update property");
      }
    } catch (error) {
      console.error("Error updating property:", error);
      setError(error.message || "Failed to update property");
      toastify("Failed to update property", "error");
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="p-fluid">
      {error && (
        <div className="alert alert-danger mb-3">
          <p className="mb-0">{error}</p>
        </div>
      )}

      <div className="row">
        {/* apartment Information Display */}
        <div className="col-12 mb-4">
          <div className="card border">
            <div className="card-body p-3">
              <h6 className="card-title mb-2">
                <i className="ti ti-building me-2 text-primary"></i>
                Editing: {unit.name}
              </h6>
              <div className="text-sm text-gray-600">
                <span>{unit.unitType} in {unit.division} • Floor: {unit.floorUnitNo}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Cancellation Policy Assignment */}
        <div className="col-12 mb-4">
          <label htmlFor="cancellationPolicyId" className="form-label">Cancellation Policy</label>
          <Dropdown
            id="cancellationPolicyId"
            value={form.cancellationPolicyId}
            options={namedCancellationPolicies.map((p) => ({ label: p.name, value: p._id }))}
            onChange={(e) => handleFormChange('cancellationPolicyId', e.value)}
            className="w-100"
            placeholder="Select a cancellation policy"
            showClear
          />
        </div>

        {/* Photos shown to guests on the booking widget / WordPress site */}
        <div className="col-12 mb-4">
          <h6 className="mb-3">
            <i className="ti ti-photo me-2 text-warning"></i>
            Photos
          </h6>
          <Divider className="mb-3" />

          {images.length === 0 && (
            <p className="text-muted small">No photos uploaded yet. Guests will see this unit without images on the booking widget.</p>
          )}

          {images.length > 0 && (
            <div className="d-flex flex-wrap gap-2 mb-3">
              {images.map((image) => (
                <div key={image.url} className="position-relative" style={{ width: "120px" }}>
                  <img
                    src={image.url}
                    alt=""
                    className="w-100 rounded border"
                    style={{ height: "90px", objectFit: "cover" }}
                  />
                  {image.isPrimary && (
                    <span className="badge bg-primary position-absolute top-0 start-0 m-1">Primary</span>
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 p-0"
                    style={{ width: "22px", height: "22px", lineHeight: "1" }}
                    onClick={() => handleRemoveImage(image.url)}
                    title="Remove photo"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}

          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleImagesSelected}
            disabled={uploadingImages || !getPropertyId()}
            className="form-control"
          />
          {!getPropertyId() && (
            <small className="text-muted d-block mt-1">Save this property first, then reopen it to add photos.</small>
          )}
          {uploadingImages && <small className="text-muted d-block mt-1">Uploading...</small>}
        </div>

        {/* Basic Information */}
        <div className="col-12">
          <h6 className="mb-3">
            <i className="ti ti-info-circle me-2 text-info"></i>
            Basic Information
          </h6>
          <Divider className="mb-4" />
        </div>

        <div className="col-md-6 mb-3">
          <label htmlFor="propertyName" className="form-label">Property Name *</label>
          <InputText
            id="propertyName"
            value={form.propertyName}
            onChange={(e) => handleFormChange('propertyName', e.target.value)}
            className="w-100"
          />
        </div>

        <div className="col-md-6 mb-3">
          <label htmlFor="propertyType" className="form-label">Property Type *</label>
          <Dropdown
            id="propertyType"
            value={form.propertyType}
            options={propertyTypes}
            onChange={(e) => handleFormChange('propertyType', e.value)}
            className="w-100"
            placeholder="Select Property Type"
          />
        </div>

        <div className="col-12 mb-3">
          <label htmlFor="description" className="form-label">Description</label>
          <InputText
            id="description"
            value={form.description}
            onChange={(e) => handleFormChange('description', e.target.value)}
            className="w-100"
          />
        </div>

        <div className="col-12 mb-3">
          <label htmlFor="amenities" className="form-label">Amenities</label>
          <MultiSelect
            id="amenities"
            value={form.amenities}
            options={amenitiesOptions}
            onChange={(e) => handleFormChange('amenities', e.value)}
            className="w-100"
            placeholder="Select Amenities"
            display="chip"
          />
        </div>

        {/* Pricing & Policies */}
        <div className="col-12">
          <h6 className="mb-3 mt-4">
            <i className="ti ti-currency-dollar me-2 text-success"></i>
            Pricing & Policies
          </h6>
          <Divider className="mb-4" />
        </div>

        <div className="col-md-6 mb-3">
          <label htmlFor="basePrice" className="form-label">Base Price *</label>
          <div className="p-inputgroup">
            <span className="p-inputgroup-addon">{getSelectedCurrencySymbol()}</span>
            <InputNumber
              id="basePrice"
              value={form.basePrice}
              onValueChange={(e) => handleFormChange('basePrice', e.value)}
              min={0}
            />
          </div>
        </div>

        <div className="col-md-6 mb-3">
          <label htmlFor="currencyId" className="form-label">Currency *</label>
          <Dropdown
            id="currencyId"
            value={form.currencyId}
            options={currencies.map(c => ({ label: `${c.name} (${c.code})`, value: c._id }))}
            onChange={(e) => handleFormChange('currencyId', e.value)}
            className="w-100"
            placeholder="Select Currency"
          />
        </div>

        <div className="col-md-6 mb-3">
          <label htmlFor="weekendPriceAdjustment" className="form-label">Weekend Price Adjustment (%)</label>
          <InputNumber
            id="weekendPriceAdjustment"
            value={form.weekendPriceAdjustment}
            onValueChange={(e) => handleFormChange('weekendPriceAdjustment', e.value)}
            className="w-100"
            suffix="%"
            min={-100}
            max={100}
          />
        </div>

        <div className="col-md-6 mb-3">
          <label htmlFor="minimumStay" className="form-label">Minimum Stay (Days)</label>
          <InputNumber
            id="minimumStay"
            value={form.minimumStay}
            onValueChange={(e) => handleFormChange('minimumStay', e.value)}
            className="w-100"
            min={1}
          />
        </div>

        <div className="col-md-6 mb-3">
          <label htmlFor="maximumStay" className="form-label">Maximum Stay (Days)</label>
          <InputNumber
            id="maximumStay"
            value={form.maximumStay}
            onValueChange={(e) => handleFormChange('maximumStay', e.value)}
            className="w-100"
            min={1}
          />
        </div>

        <div className="col-md-6 mb-3">
          <label htmlFor="advanceBookingDays" className="form-label">Advance Booking Days</label>
          <InputNumber
            id="advanceBookingDays"
            value={form.advanceBookingDays}
            onValueChange={(e) => handleFormChange('advanceBookingDays', e.value)}
            className="w-100"
            min={0}
          />
        </div>

        <div className="col-md-6 mb-3">
          <label htmlFor="commission" className="form-label">Default Commission (%) *</label>
          <InputNumber
            id="commission"
            value={form.commission}
            onValueChange={(e) => handleFormChange('commission', e.value)}
            className="w-100"
            suffix="%"
            min={0}
            max={100}
          />
          <small className="text-muted d-block mt-1">
            Used whenever a stay doesn't match any tier below. Internal-only, never shown to guests.
          </small>
        </div>

        <div className="col-12 mb-3">
          <div className="border rounded p-3 bg-light">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <label className="form-label mb-0 fw-semibold">
                <i className="ti ti-stairs me-2 text-primary"></i>
                Commission Tiers by Length of Stay (Optional)
              </label>
              <Button
                label="Add Tier"
                icon="ti ti-plus"
                className="p-button-sm p-button-outlined"
                onClick={handleAddCommissionTier}
              />
            </div>
            <small className="text-muted d-block mb-3">
              E.g. 1-2 nights at 5%, 3-6 nights at 10%, 7+ nights at 20% - or a fixed currency amount per tier.
              Internal only, never shown to the guest.
            </small>

            {commissionTiers.length === 0 ? (
              <div className="text-muted small">No tiers added - the default commission above will always apply.</div>
            ) : (
              commissionTiers.map((tier, index) => (
                <div className="row align-items-end mb-2" key={index}>
                  <div className="col-3">
                    <label className="form-label small mb-1">Min Nights</label>
                    <InputNumber
                      value={tier.minNights}
                      onValueChange={(e) => handleCommissionTierChange(index, 'minNights', e.value)}
                      className="w-100"
                      min={1}
                    />
                  </div>
                  <div className="col-3">
                    <label className="form-label small mb-1">Max Nights</label>
                    <InputNumber
                      value={tier.maxNights}
                      onValueChange={(e) => handleCommissionTierChange(index, 'maxNights', e.value)}
                      className="w-100"
                      min={tier.minNights || 1}
                      placeholder="No limit"
                    />
                  </div>
                  <div className="col-3">
                    <label className="form-label small mb-1">Type</label>
                    <Dropdown
                      value={tier.type}
                      options={COMMISSION_TYPE_OPTIONS}
                      onChange={(e) => handleCommissionTierChange(index, 'type', e.value)}
                      className="w-100"
                    />
                  </div>
                  <div className="col-2">
                    <label className="form-label small mb-1">Value</label>
                    <InputNumber
                      value={tier.value}
                      onValueChange={(e) => handleCommissionTierChange(index, 'value', e.value)}
                      className="w-100"
                      min={0}
                      suffix={tier.type === 'percentage' ? '%' : undefined}
                    />
                  </div>
                  <div className="col-1">
                    <Button
                      icon="ti ti-trash"
                      className="p-button-danger p-button-sm"
                      onClick={() => handleRemoveCommissionTier(index)}
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="col-12 d-flex justify-content-end gap-2 mt-4">
          <Button label="Cancel" icon="ti ti-x" className="p-button-text" onClick={onHide} />
          <Button
            label="Update"
            icon="ti ti-check"
            className="p-button-primary"
            onClick={handleSubmit}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};

export default EditBookingPropertyForm;